<template>
    <article class="rounded-2xl border border-foxy-border/80 bg-foxy-surface/75 p-4 shadow-sm">
        <div class="flex flex-col items-stretch gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                    <h4 class="min-w-0 text-sm">
                        <span class="font-extrabold text-foxy-text">{{ resultLabel }}</span>
                        <span v-if="hasRating" class="font-semibold text-foxy-text-secondary"> · {{ reading.rating }}/10</span>
                        <span v-else-if="showUnratedRatingAffordance" class="font-semibold text-foxy-text-secondary">
                            · Не оцінено ·
                            <button
                                type="button"
                                class="text-xs font-bold text-foxy-action underline underline-offset-2 transition hover:opacity-90"
                                @click="$emit('edit', reading, $event)"
                            >
                                Оцінити
                            </button>
                        </span>
                    </h4>
                    <span v-if="reading.imported" class="acc-pill !px-2 !py-0.5 text-[11px]">Імпортовано</span>
                </div>

                <div v-if="dateMetadata.length" class="mt-2 space-y-1 text-xs text-foxy-text-secondary">
                    <div v-for="detail in dateMetadata" :key="detail">{{ detail }}</div>
                </div>

                <div v-if="metadataGroups.length" class="mt-2 flex flex-wrap gap-x-2 gap-y-1 text-xs text-foxy-text-secondary">
                    <template v-for="(metadata, index) in metadataGroups">
                        <span v-if="index" :key="`metadata-${metadata.key}-separator`" aria-hidden="true">·</span>
                        <span :key="`metadata-${metadata.key}-value`">{{ metadata.label }}</span>
                    </template>
                </div>

                <p v-if="!hasDisplayableDetails" class="mt-2 text-xs text-foxy-text-secondary">Для цього читання немає детальних даних.</p>

                <a
                    v-if="publicationUrl"
                    class="mt-3 inline-flex min-h-11 items-center rounded-xl border border-foxy-border px-3 py-2 text-xs font-bold text-foxy-action transition hover:bg-foxy-elevated"
                    :href="publicationUrl"
                >
                    Відкрити {{ publicationTypeLabel }}
                </a>
            </div>

            <div class="flex shrink-0 sm:self-start">
                <button
                    ref="editButton"
                    type="button"
                    :data-reading-edit-id="reading.id"
                    class="acc-btn min-h-11 justify-center !px-3 !py-2 text-xs"
                    :disabled="busy"
                    :aria-label="editLabel"
                    @click="$emit('edit', reading, $event)"
                >
                    Редагувати
                </button>
            </div>
        </div>
        <slot></slot>
    </article>
</template>

<script lang="ts">
export default {
    name: 'LibraryReadingEntry',
    props: {
        reading: { type: Object, required: true },
        busy: { type: Boolean, default: false },
        languageLabel: { type: Function, required: true },
        showUnratedRatingAffordance: { type: Boolean, default: false },
    },
    computed: {
        resultLabel() {
            if (this.reading.state === 'active') return 'Читаю';
            if (this.reading.state === 'paused') return 'Призупинено';
            return this.reading.state_label || (this.reading.state === 'abandoned' ? 'Не дочитано' : 'Прочитано');
        },
        dateMetadata() {
            const details = [];
            if (this.isKnown(this.reading.start)) details.push(`Початок: ${this.reading.start.label}`);
            if (this.isKnown(this.reading.finish)) details.push(`Завершено: ${this.reading.finish.label}`);
            return details;
        },
        languageNames() {
            return (this.reading.languages || [])
                .map((language) => language.label || this.languageLabel(language.code) || String(language.code || '').toUpperCase())
                .filter(Boolean);
        },
        formatNames() {
            return (this.reading.formats || [])
                .map((format) => (format.code === 'other' && format.custom_label ? format.custom_label : format.label || format.code))
                .filter(Boolean);
        },
        metadataGroups() {
            return [
                { key: 'languages', label: this.languageNames.join(', ') },
                { key: 'formats', label: this.formatNames.join(', ') },
            ].filter((metadata) => Boolean(metadata.label));
        },
        hasRating() {
            return Number.isInteger(this.reading.rating) && this.reading.rating > 0;
        },
        hasDisplayableDetails() {
            return Boolean(this.dateMetadata.length || this.hasRating || this.metadataGroups.length || this.publicationUrl);
        },
        publicationUrl() {
            return this.reading.publication?.is_navigable && this.reading.publication?.url ? this.reading.publication.url : '';
        },
        publicationTypeLabel() {
            if (this.reading.publication?.type === 'opinion') return 'Відгук';
            if (this.reading.publication?.type === 'review') return 'Рецензію';
            return this.reading.publication?.type_label || 'публікацію';
        },
        editLabel() {
            const dateContext = this.dateMetadata.join(', ');
            return `Редагувати прочитання: ${this.resultLabel}${dateContext ? `, ${dateContext}` : ''}`;
        },
    },
    methods: {
        isKnown(partial) {
            return Boolean(partial && partial.precision !== 'unknown' && partial.label);
        },
    },
};
</script>
